import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { Input } from '../ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Trash2 } from 'lucide-react';
import { computeBlockColor } from './colorUtils';

/** Swatch edge, in px. Sized against the 20px label text rather than the old 12px default. */
const SWATCH_PX = 16;

/**
 * The row's count, popping each time it changes so a click on a block edge lands somewhere the
 * student is already looking. Both the number and the blocks answer "how many", and only one of
 * them is a numeral.
 *
 * Keyed on a change counter rather than on `total` itself: remounting on the value would also
 * animate on first paint and on every arrangement pivot, popping numbers nobody touched.
 */
function PulsingTotal({ total }: { total: number }) {
  const [changeCount, setChangeCount] = useState(0);
  const previousTotal = useRef(total);

  useEffect(() => {
    if (previousTotal.current === total) {
      return;
    }
    previousTotal.current = total;
    setChangeCount(count => count + 1);
  }, [total]);

  return (
    <motion.span
      key={changeCount}
      // Nothing has changed yet on the first render of a given mount, so there is nothing to
      // announce; `false` skips the entrance animation entirely rather than animating to a resting
      // state the number is already in.
      initial={changeCount === 0 ? false : { scale: 1.5 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      // Scale has no effect on an inline box.
      style={{ display: 'inline-block' }}
    >
      {total}
    </motion.span>
  );
}

interface EditableLabelProps {
  value: string;
  color: string;
  total: number;
  onChange: (newValue: string) => void;
  onDelete?: () => void;
  canDelete?: boolean;
  /**
   * Persistently highlight this label as editable. Used by the Explore tutorial: the ordinary
   * "you can click this" affordance is a hover background, and hover never fires on a touchscreen.
   */
  pulse?: boolean;
}

export function EditableLabel({
  value,
  color,
  total,
  onChange,
  onDelete,
  canDelete,
  pulse = false,
}: EditableLabelProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isHovering, setIsHovering] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Compute the effective color based on the label
  const { color: effectiveColor, isLight } = computeBlockColor(value, color);

  // Hover and the open editor both win over the pulse, matching `Block`: once the student has
  // reached the target, they get the usual confirmation rather than an animation that ignores them.
  const isPulsing = pulse && !isHovering && !isEditing;

  // Same 1s cadence and green as the block-edge and control-button pulses, so every "here" the
  // tutorial points at reads as one language.
  const pulseMotion = isPulsing
    ? {
        animate: {
          scale: [1, 1.06, 1],
          boxShadow: [
            '0 0 0 0 rgba(34, 197, 94, 0)',
            '0 0 0 8px rgba(34, 197, 94, 0.35)',
            '0 0 0 0 rgba(34, 197, 94, 0)',
          ],
        },
        transition: { duration: 1, repeat: Infinity, ease: 'easeInOut' as const },
      }
    : {
        animate: { scale: 1, boxShadow: '0 0 0 0 rgba(34, 197, 94, 0)' },
        transition: { duration: 0.2 },
      };

  const handleStartEditing = () => {
    setEditValue(value);
    setIsEditing(true);
    // Use setTimeout to ensure the input is visible before focusing
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 0);
  };

  const handleFinishEditing = () => {
    if (editValue.trim()) {
      onChange(editValue.trim());
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleFinishEditing();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the edit mode
    if (onDelete) {
      onDelete();
    }
  };

  return (
    <div
      className="tw-flex tw-items-center tw-gap-2"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Color swatch container with delete button overlay */}
      <div className="tw-relative tw-flex-shrink-0" style={{ width: `${SWATCH_PX}px`, height: `${SWATCH_PX}px` }}>
        {/* Color swatch - always rendered */}
        <div
          className="tw-absolute tw-inset-0"
          style={{
            backgroundColor: effectiveColor,
            borderRadius: 'var(--radius-sm)',
            // For light colors, add a visible border
            border: isLight ? '1px solid var(--border)' : 'none',
            opacity: isHovering && canDelete ? 0.3 : 1,
            transition: 'opacity 0.2s ease-in-out',
          }}
        />

        {/* Delete button - overlays the swatch when hovering */}
        {isHovering && canDelete && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleDelete}
                className="tw-absolute tw-bg-white tw-text-neutral-500 hover:tw-text-red-600 tw-transition-colors tw-cursor-pointer"
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  border: '1px solid var(--border)',
                  boxShadow: 'var(--elevation-sm)',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                }}
              >
                <Trash2 className="tw-w-3.5 tw-h-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <span className="tw-text-white">Delete {value || 'Value'}</span>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Display mode */}
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.div
            className={`tw-cursor-pointer tw-px-2 tw-py-1 tw-rounded tw-whitespace-nowrap ${
              isEditing ? 'tw-hidden' : 'tw-flex tw-items-center tw-gap-1'
            }`}
            onClick={handleStartEditing}
            style={{
              borderRadius: 'var(--radius-sm)',
              backgroundColor: isHovering ? 'var(--primary)' : 'transparent',
              transition: 'background-color 0.2s ease-in-out',
            }}
            {...pulseMotion}
          >
            <span
              style={{
                fontFamily: 'var(--font-atkinson)',
                fontSize: 'var(--text-xl)',
                color: isHovering ? 'var(--primary-foreground)' : 'var(--foreground)',
                transition: 'color 0.2s ease-in-out',
              }}
            >
              {value}: <PulsingTotal total={total} />
            </span>
          </motion.div>
        </TooltipTrigger>
        <TooltipContent>
          <span className="tw-text-white">Edit Label</span>
        </TooltipContent>
      </Tooltip>

      {/* Edit mode */}
      <div className={`tw-flex tw-items-center tw-gap-1 tw-whitespace-nowrap ${isEditing ? 'tw-flex' : 'tw-hidden'}`}>
        <Input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={handleFinishEditing}
          onKeyDown={handleKeyDown}
          className="tw-h-10 tw-px-2 tw-py-1 tw-w-40"
          style={{ borderRadius: 'var(--radius)', fontSize: 'var(--text-xl)' }}
        />
        <span className="tw-text-neutral-950" style={{ fontSize: 'var(--text-xl)' }}>
          : <PulsingTotal total={total} />
        </span>
      </div>
    </div>
  );
}
